import { useCallback, useEffect, useState } from "react";
import { requestPost } from "shared-utils";

import type { BankConnectionSchema } from "../api-types/bankconnect_schemas";

import { BasiqClient } from "./basiq/basiq-client";

export interface BankConnectionLogo {
  loading: boolean;
  couldLoad?: boolean;
  data?: string;
  type: "base64" | "url";
  cached: boolean;
}

export interface LogoLoadingState {
  [instituteID: string]: BankConnectionLogo;
}

export interface LogoResult {
  data: string;
  type: "url" | "base64";
}

export type LogoResolver = (bankConnection: BankConnectionSchema) => Promise<LogoResult>;

export interface LogoResolvers {
  [providerName: string]: LogoResolver;
}

export interface UseBankConnectionLogoResolversProps {
  getInstitutionLogoPath: string;
  basiqClient: BasiqClient;
}

export function useBankConnectionLogoResolvers({
  getInstitutionLogoPath,
  basiqClient,
}: UseBankConnectionLogoResolversProps): LogoResolvers {
  const plaidLogoResolver: LogoResolver = useCallback(
    async (connection: BankConnectionSchema) => {
      const result = await requestPost(getInstitutionLogoPath, { institutionID: connection.institutionID });
      return { data: result.image, type: "base64" };
    },
    [getInstitutionLogoPath]
  );

  const basiqLogoResolver: LogoResolver = useCallback(
    async (connection: BankConnectionSchema) => {
      const result = await basiqClient.getInstitution(connection.institutionID);
      return { data: result.logo?.links?.square, type: "url" };
    },
    [basiqClient]
  );

  const mockedLogoResolver: LogoResolver = useCallback(() => {
    // Return empty data for mocked connections - they will use a fallback icon instead
    return Promise.resolve({ data: "", type: "url" as const });
  }, []);

  return {
    plaid: plaidLogoResolver,
    basiq: basiqClient ? basiqLogoResolver : null,
    mocked: mockedLogoResolver,
  };
}

type UseBankConnectionLogosProps = {
  bankConnections: BankConnectionSchema[];
  resolvers: LogoResolvers;
};

/**
 *
 * React Hook that maintains a map of logos (base64 encoded) for a given set of bank connections
 * The logos have to be fetched separately to the connections themselves.
 */
export function useBankConnectionLogos({ bankConnections, resolvers }: UseBankConnectionLogosProps) {
  const [logoLoadingStates, setLogoLoadingStates] = useState<LogoLoadingState>({});

  const loadLogo = useCallback(
    async (bankConnection: BankConnectionSchema) => {
      const setNoLogoForInstitution = (institutionID) => {
        setLogoLoadingStates((nextLogoLoadingStates) =>
          Object.assign({}, nextLogoLoadingStates, { [institutionID]: { loading: false, couldLoad: false } })
        );
      };

      try {
        const logoResolver = resolvers[bankConnection.providerType];
        if (!logoResolver) {
          return setNoLogoForInstitution(bankConnection.institutionID);
        }

        const { data, type } = await logoResolver(bankConnection);

        setLogoLoadingStates((nextLogoLoadingStates) =>
          Object.assign({}, nextLogoLoadingStates, {
            [bankConnection.institutionID]: { loading: false, couldLoad: !!data, data, type },
          })
        );
      } catch {
        setNoLogoForInstitution(bankConnection.institutionID);
      }
    },
    [resolvers]
  );

  useEffect(() => {
    bankConnections.forEach((connection) => {
      setLogoLoadingStates((nextLogoLoadingStates) => {
        if (nextLogoLoadingStates[connection.institutionID]) {
          // already loading or failed to load, do nothing
          return nextLogoLoadingStates;
        }
        if (connection.logo) {
          return Object.assign({}, nextLogoLoadingStates, {
            [connection.institutionID]: {
              loading: false,
              couldLoad: true,
              data: connection.logo.data,
              type: connection.logo.type,
            },
          });
        }
        loadLogo(connection);
        return Object.assign({}, nextLogoLoadingStates, { [connection.institutionID]: { loading: true } });
      });
    });
  }, [bankConnections, loadLogo]);

  return { logoLoadingStates };
}
